import { Fragment, useEffect, useRef, type ReactNode } from "react";
import { Button, IconButton, Spinner } from "@NP-Webber/crm-kit/dist/next/index.js";
import type { AiFeedbackSurface } from "../../../api/aiApi";
import { AiFeedbackButtons } from "../feedback/AiFeedbackButtons";
import "./agentChat.css";

/**
 * AgentChat — the conversation with an AI agent (Turbo's dataEntry/AgentChat, shared by «הזנה חכמה»
 * and «בדיקת נתונים»). Messages: { id, role: 'user' | 'agent' | 'note', text, steps?, actions?, error? }.
 * The agent's actions arrive as buttons; the parent runs them (onAction) and marks them done.
 * feedback: { surface } — 👍/👎 under each agent answer (null = none).
 */
export type AgentChatAction = {
  type: string;
  label: string;
  icon?: string;
  primary?: boolean;
  done?: boolean;
  doneLabel?: string;
  busy?: boolean;
  [key: string]: unknown;
};

export type AgentChatMessage = {
  id: string;
  role: "user" | "agent" | "note";
  text: string;
  steps?: string[];
  actions?: AgentChatAction[];
  error?: boolean;
};

export type AgentChatProps = {
  messages: AgentChatMessage[];
  busy?: boolean;
  onSend: (text: string) => void;
  onAction?: (action: AgentChatAction, message: AgentChatMessage) => void;
  starters?: string[];
  draft: string;
  setDraft: (text: string) => void;
  title?: string;
  hint?: ReactNode;
  onClear?: () => void;
  emptyText?: ReactNode;
  placeholder?: string;
  feedback?: { surface: AiFeedbackSurface } | null;
};

const newChatId = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;

/** tiny, safe formatting: **bold**, bullet / numbered lines, headings, paragraphs — no HTML from the model */
function inline(text: string, keyBase: string | number): ReactNode[] {
  return String(text)
    .split(/(\*\*[^*]+\*\*)/g)
    .map((p, i) =>
      p.startsWith("**") && p.endsWith("**") && p.length > 4 ? (
        <b key={`${keyBase}-${i}`}>{p.slice(2, -2)}</b>
      ) : (
        <Fragment key={`${keyBase}-${i}`}>{p}</Fragment>
      ),
    );
}

type Block = { type: "gap" } | { type: "ul" | "ol"; items: string[] } | { type: "h" | "p"; text: string };

export function AgentText({ text }: { text: string }) {
  const lines = String(text || "").replace(/\r/g, "").split("\n");
  const blocks: Block[] = [];
  let list: { type: "ul" | "ol"; items: string[] } | null = null;
  lines.forEach((raw) => {
    const line = raw.trimEnd();
    const bullet = /^\s*(?:[-•*]|\d+[.)])\s+(.*)$/.exec(line);
    if (bullet) {
      if (!list) {
        list = { type: /^\s*\d/.test(line) ? "ol" : "ul", items: [] };
        blocks.push(list);
      }
      list.items.push(bullet[1]!);
      return;
    }
    list = null;
    if (!line.trim()) {
      blocks.push({ type: "gap" });
      return;
    }
    const head = /^#{1,4}\s+(.*)$/.exec(line);
    blocks.push({ type: head ? "h" : "p", text: head ? head[1]! : line });
  });
  return (
    <div className="de-chat__text">
      {blocks.map((b, i) => {
        if (!("items" in b) && !("text" in b)) return <span key={`g${i}`} className="de-chat__gap" />;
        if ("items" in b) {
          const items = b.items.map((it, j) => <li key={j}>{inline(it, `${i}-${j}`)}</li>);
          return b.type === "ol" ? <ol key={`l${i}`}>{items}</ol> : <ul key={`l${i}`}>{items}</ul>;
        }
        const content = inline(b.text, i);
        return b.type === "h" ? (
          <b key={`h${i}`} className="de-chat__h">
            {content}
          </b>
        ) : (
          <p key={`p${i}`}>{content}</p>
        );
      })}
    </div>
  );
}

export function AgentChat({
  messages,
  busy = false,
  onSend,
  onAction,
  starters = [],
  draft,
  setDraft,
  title = "שיחה עם הסוכן",
  hint,
  onClear,
  emptyText,
  placeholder = "למשל: «כל השורות עם ביט הן הכנסות» · «למה שורה 12 היא הוצאה?»",
  feedback = { surface: "data_entry" },
}: AgentChatProps) {
  const listRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  // message ids restart with a new conversation — the feedback key gets a new chat id then
  const chatIdRef = useRef(newChatId());
  if (!messages.length) chatIdRef.current = newChatId();

  const questionFor = (m: AgentChatMessage) => {
    const i = messages.indexOf(m);
    return [...messages.slice(0, i)].reverse().find((x) => x.role === "user")?.text ?? "";
  };
  const answerFor = (m: AgentChatMessage) => [m.steps?.length ? `(${m.steps.join(" · ")})` : null, m.text].filter(Boolean).join("\n");

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, busy]);
  useEffect(() => {
    inputRef.current?.focus();
  }, []);
  useEffect(() => {
    // a question about a row arrives as a draft — put the cursor at its end
    const el = inputRef.current;
    if (draft && el && document.activeElement !== el) {
      el.focus();
      const n = el.value.length;
      el.setSelectionRange(n, n);
    }
  }, [draft]);

  const send = (text?: string) => {
    const t = String(text ?? draft ?? "").trim();
    if (!t || busy) return;
    onSend(t);
    setDraft("");
  };

  return (
    <div className="de-chat">
      <div className="de-chat__head">
        <span className="de-chat__av" aria-hidden="true">
          ✦
        </span>
        <div className="de-chat__title">
          <b>{title}</b>
          {hint ? <span className="de-muted">{hint}</span> : null}
        </div>
        <span className="ck-grow" />
        {onClear && messages.length ? <IconButton size="sm" icon="refresh" label="שיחה חדשה" onClick={onClear} /> : null}
      </div>

      <div className="de-chat__list" ref={listRef} aria-live="polite">
        {!messages.length ? (
          <div className="de-chat__empty">
            <p>{emptyText || "כתבו לסוכן כמו לעובד: מה לעשות, מה לתקן, או מה לא ברור."}</p>
            {starters.length ? (
              <div className="de-chat__starters">
                {starters.map((s) => (
                  <button key={s} type="button" className="de-chat__starter" onClick={() => send(s)}>
                    {s}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
        {messages.map((m) => (
          <div key={m.id} className={`de-msg de-msg--${m.role}${m.error ? " is-error" : ""}`}>
            {m.role === "agent" ? (
              <span className="de-msg__av" aria-hidden="true">
                ✦
              </span>
            ) : null}
            <div className="de-msg__body">
              {m.steps?.length ? <div className="de-msg__steps">{m.steps.join(" · ")}</div> : null}
              {m.role === "user" ? <p className="de-msg__plain">{m.text}</p> : <AgentText text={m.text} />}
              {m.actions?.length ? (
                <div className="de-msg__actions">
                  {m.actions.map((a, i) => (
                    <Button
                      key={`${a.type}-${i}`}
                      size="sm"
                      variant={a.done ? "ghost" : a.primary ? "primary" : undefined}
                      icon={a.done ? "check" : a.icon}
                      disabled={a.done || a.busy || busy}
                      onClick={() => onAction?.(a, m)}
                    >
                      {a.done ? a.doneLabel || a.label : a.label}
                    </Button>
                  ))}
                </div>
              ) : null}
              {feedback?.surface && m.role === "agent" && !m.error && m.text ? (
                <AiFeedbackButtons
                  surface={feedback.surface}
                  refKey={`${chatIdRef.current}:${m.id}`}
                  question={questionFor(m).slice(0, 4000)}
                  answer={answerFor(m).slice(0, 8000)}
                />
              ) : null}
            </div>
          </div>
        ))}
        {busy ? (
          <div className="de-msg de-msg--agent is-busy">
            <span className="de-msg__av" aria-hidden="true">
              ✦
            </span>
            <div className="de-msg__body">
              <span className="de-chat__typing">
                <Spinner size={14} /> הסוכן בודק…
              </span>
            </div>
          </div>
        ) : null}
      </div>

      <div className="de-chat__composer">
        <textarea
          ref={inputRef}
          className="de-chat__input"
          rows={Math.min(5, Math.max(1, String(draft || "").split("\n").length))}
          value={draft || ""}
          placeholder={placeholder}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              e.stopPropagation();
              send();
            }
          }}
          aria-label="הודעה לסוכן"
        />
        <Button variant="primary" icon="left" onClick={() => send()} disabled={busy || !String(draft || "").trim()}>
          שליחה
        </Button>
      </div>
    </div>
  );
}

export default AgentChat;
